import React, { useEffect, useMemo, useState } from "react";
import { useTable, useSortBy, usePagination, Column } from 'react-table';
import * as dayjs from 'dayjs';

// API
import api from "../../../../api/Base";

// Utils
import { showError } from '../../../../utils/showAlert';

// Components
import TablePagination from '../../../../component/TablePagination';
import NoData from '../../../../component/NoData';
import ConditionalRender from "../../../../helpers/ConditionalRender";

// Helpers
import { getDefaultPageSize, getTimeZone } from '../../../../helpers/utils';
import { API_DATE_FORMAT } from '../../../../helpers/timeRangeUtils';

// Styles
import "./AgentServerPanel.scss";
import Helper from "../../../../helpers/Helper";

interface AgentServerPanelProps {
    open: boolean;
    server: any;
    onClose: () => void;
    instanceType: string;
}

const AgentServerPanel: React.FC<AgentServerPanelProps> = ({ open, server, onClose, instanceType }) => {
    const [steps, setSteps] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());
            useEffect(() => {
                const fetchSteps = async () => {
                    if (!server || !server.jobid || !server.timeslice) return;
        
                    setLoading(true);
                    try {
                        const tz = getTimeZone();
                        const isUTC = tz === 'UTC';
                        const jobStartTime = (isUTC ? dayjs.utc(server.timeslice) : dayjs.utc(server.timeslice).tz(tz));
                        const jobDurationMs = Math.max((server.runduration || 0) * 1000, 1000) + 1000;
                        const jobEndTime = jobStartTime.add(jobDurationMs, 'millisecond');
        
                        const from = jobStartTime.format(API_DATE_FORMAT);
                        const to = jobEndTime.format(API_DATE_FORMAT);
        
                        const response = await api.get(`job/history/step?from=${from}&to=${to}&tz=${tz}&jobid=${server.jobid}&type=${instanceType}&id=${server.id}`);
                        setSteps(Array.isArray(response.data) ? response.data : []);
                    } catch (error: any) {
                        console.error('Failed to retrieve job history step data:', error);
                        showError('Failed to retrieve job history step data', error.message || error.toString());
                        setSteps([]);
                    } finally {
                        setLoading(false);
                    }
                };
        
                if (open && server) {
                    void fetchSteps();
                }
            }, [open, server, instanceType]);

    // Toggle row expansion
    const toggleRowExpansion = (stepId: number) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(stepId)) {
                newSet.delete(stepId);
            } else {
                newSet.add(stepId);
            }
            return newSet;
        });
    };

    // React Table columns definition
    const columns: Column[] = useMemo(() => [
        {
            Header: '',
            id: 'expander',
            className: 'text-center',
            headerClassName: 'text-center',
            width: 50,
            Cell: ({ row }: any) => {
                const stepId = row.original.stepid;
                const isExpanded = expandedRows.has(stepId);
                return (
                    <button
                        className="btn btn-sm btn-link p-0"
                        onClick={() => toggleRowExpansion(stepId)}
                        aria-label={isExpanded ? 'Collapse row' : 'Expand row'}
                        style={{ fontSize: '14px', color: 'var(--dbm-text-muted)' }}
                    >
                        <i className={`fal fa-chevron-${isExpanded ? 'down' : 'right'}`} />
                    </button>
                );
            }
        },
        {
            Header: 'Result',
            accessor: 'statustext',
            canSort: true,
            className: 'text-center',
            headerClassName: 'text-center',
            Cell: ({ value }: { value: string }) => {
                const isSuccess = value?.toLowerCase().includes('succeed');
                const isFailed = value?.toLowerCase().includes('fail');
                return (
                        <span className={isSuccess ? 'text-success' : isFailed ? 'text-danger' : ''}>
                        {isSuccess && <i className="fal fa-check-circle fa-fw"/>}
                            {isFailed && <i className="fal fa-times-circle fa-fw"/>}
                    </span>
                );
            }
        },
        {
            Header: 'Step ID',
            accessor: 'stepid',
            className: 'text-center',
            headerClassName: 'text-center',
            canSort: true
        },
        {
            Header: 'Step Name',
            accessor: 'stepname',
            className: 'text-center',
            headerClassName: 'text-center',
            canSort: true
        },
        {
            Header: 'Message',
            accessor: 'message',
            canSort: true,
        },
        {
            Header: 'Duration (s)',
            accessor: 'duration',
            className: 'text-end',
            headerClassName: 'text-end',
            canSort: true,
            Cell: ({cell}: any) => Number(cell.row.values.duration) === 0 ? '0' : (Helper.getTimeInEnglish(cell.row.values.duration))
        }
    ], [expandedRows]);

    // Move row with stepid=0 (Job outcome/total) to the end
    const dataWithTotalAtEnd = useMemo(() => {
        if (steps.length === 0) return [];

        // Find the row with stepid=0 (the total/job outcome)
        const totalRow = steps.find(step => step.stepid === 0);
        const otherRows = steps.filter(step => step.stepid !== 0);

        if (!totalRow) return steps;

        // Move total to the end and mark it
        return [...otherRows, { ...totalRow, isTotal: true }];
    }, [steps]);

    // React Table configuration
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize }
    } = useTable(
        {
            columns,
            data: dataWithTotalAtEnd,
            initialState: {
                pageIndex: 0,
                pageSize: getDefaultPageSize(),
                sortBy: []
            }
        },
        useSortBy,
        usePagination
    );

    if (!server) return null;

    return (
        <div
            aria-hidden={!open}
            tabIndex={-1}
            role="dialog"
            className={`agent-server-details-panel ${open ? '' : 'hidden'}`}
        >
            <div className="panel-header">
                <span className='header-filters'>
                    <i
                        className="fa fa-server"
                        style={{ marginRight: 6 }}
                        aria-hidden="true"
                    />
                    <span>
                        {server?.jobname || 'Unknown Job'}
                        <span style={{ fontWeight: 400, fontSize: 14, color: "var(--dbm-text-muted)", marginLeft: 4 }}>
                            (Job)
                        </span>
                    </span>
                    <span>
                        {server?.server || 'Unknown Server'}
                        <span style={{ fontWeight: 400, fontSize: 14, color: "var(--dbm-text-muted)", marginLeft: 4 }}>
                            (Server)
                        </span>
                    </span>
                </span>
                <button className="close-button" onClick={onClose} aria-label="Close panel">
                    &times;
                </button>
            </div>

            <div className="details-content">
                <div className="card">
                    <div className="card-header">
                        <i className="fal fa-list-ol fa-fw" aria-hidden="true"/>
                        Job Steps
                        <span className="badge bg-info ms-2">
                            {steps.length}
                        </span>
                    </div>
                    <div className="card-body">
                        <ConditionalRender if={loading}>
                            <div className="w-100 text-center text-muted my-3">
                                <div className="loader spinner">
                                </div>
                                <p className="mt-3">
                                    Loading job steps...
                                </p>
                            </div>
                        </ConditionalRender>

                        <ConditionalRender if={!loading}>
                            <div className="table-responsive">
                                <table {...getTableProps()} className="table">
                                    <thead>
                                    {headerGroups.map((headerGroup) => (
                                        <tr {...headerGroup.getHeaderGroupProps()}>
                                            {headerGroup.headers.map((column) => (
                                                <th {...column.getHeaderProps((column as any).getSortByToggleProps())}
                                                    className={(column as any).headerClassName}>
                                                    {column.render('Header')}
                                                    {(column as any).canSort ? ((column as any).isSorted ? ((column as any).isSortedDesc ?
                                                        <i className="fal fa-sort-amount-up-alt"></i> :
                                                        <i className="fal fa-sort-amount-down-alt"></i>) :
                                                        <i className="fal fa-sort-amount-down-alt text-light"></i>) : ''}
                                                </th>
                                            ))}
                                        </tr>
                                    ))}
                                    </thead>
                                    <tbody {...getTableBodyProps()}>
                                    {page.map((row) => {
                                        prepareRow(row);
                                        const isTotal = (row.original as any).isTotal;
                                        const isExpanded = expandedRows.has((row.original as any).stepid);

                                        return (
                                            <React.Fragment key={row.id}>
                                                <tr {...row.getRowProps()}
                                                    style={isTotal ? { fontWeight: 'bold', borderTop: '2px solid var(--dbm-border-color)' } : {}}>
                                                    {row.cells.map((cell) => {
                                                        return (
                                                            <td {...cell.getCellProps()}
                                                                className={(cell.column as any).className}>
                                                                {cell.render('Cell')}
                                                            </td>
                                                        )
                                                    })}
                                                </tr>
                                                {isExpanded && (
                                                    <tr className="expanded-row-details">
                                                        <td colSpan={columns.length} style={{
                                                            backgroundColor: 'var(--dbm-bg-surface-alt)',
                                                            color: 'var(--dbm-text-primary)',
                                                            padding: '12px 20px',
                                                            borderBottom: '1px solid var(--dbm-border-color)'
                                                        }}>
                                                            <div className="row g-3">
                                                                <div className="col-md-4">
                                                                    <strong>Job ID:</strong>
                                                                    <div className="text-muted small" style={{ wordBreak: 'break-all' }}>
                                                                        {(row.original as any).jobid || 'N/A'}
                                                                    </div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>Server:</strong>
                                                                    <div className="text-muted">{(row.original as any).server ?? 'N/A'}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>SQL Message ID:</strong>
                                                                    <div className="text-muted">{(row.original as any).sqlmessageid ?? 'N/A'}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>SQL Severity:</strong>
                                                                    <div className="text-muted">{(row.original as any).sqlseverity ?? 'N/A'}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>Operator ID Emailed:</strong>
                                                                    <div className="text-muted">{(row.original as any).operatoridemailed ?? 'N/A'}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>Operator ID Netsent:</strong>
                                                                    <div className="text-muted">{(row.original as any).operatoridnetsent ?? 'N/A'}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>Operator ID Paged:</strong>
                                                                    <div className="text-muted">{(row.original as any).operatoridpaged ?? 'N/A'}</div>
                                                                </div>
                                                                <div className="col-md-4">
                                                                    <strong>Retries Attempted:</strong>
                                                                    <div className="text-muted">{(row.original as any).retriesattempted ?? 'N/A'}</div>
                                                                </div>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        )
                                    })}
                                    </tbody>
                                </table>
                            </div>
                            <NoData
                                error={null}
                                loading={loading}
                                length={steps.length}
                            />
                            <TablePagination
                                length={steps.length}
                                pageSize={pageSize}
                                setPageSize={setPageSize}
                                pageOptions={pageOptions}
                                pageCount={pageCount}
                                canPreviousPage={canPreviousPage}
                                previousPage={previousPage}
                                canNextPage={canNextPage}
                                nextPage={nextPage}
                                gotoPage={gotoPage}
                                pageIndex={pageIndex}
                            />
                        </ConditionalRender>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AgentServerPanel;

